import { IProductModel, isAnnualPeriod } from "@nf/common";
import { CountryCode } from "@nf/constants";
import { useTranslation } from "next-i18next";
import cx from "classnames";

import Button from "components/Button";
import { PERIOD_LABEL } from "components/OfferCard";
import OfferBadge from "components/OfferCard/CardComponents/OfferBadge";
import OfferPrice from "components/OfferCard/CardComponents/OfferPrice";
import { useOfferSavingsText } from "components/OfferCard/hooks";
import { useCountry, useSignUpUrl } from "hooks";

import styles from "./LandingPageOffer.module.scss";

const PRICE_EXPLANATION: Record<CountryCode, string> = {
  [CountryCode.Danish]: "web_landing_page_annual_price_explanation_denmark",
  [CountryCode.Finnish]: "web_landing_page_annual_price_explanation_finland",
};

interface LandingPageOfferProps {
  offer: IProductModel;
}

const LandingPageOffer = ({ offer }: LandingPageOfferProps) => {
  const { t } = useTranslation("translations");
  const country = useCountry();
  const signUpUrl = useSignUpUrl();
  const savings = useOfferSavingsText(offer);

  const isAnnual = isAnnualPeriod(offer.Period);

  const periodTranslationKey = PERIOD_LABEL[offer.Period];
  const priceExplanationTranslationKey =
    PRICE_EXPLANATION[country.toUpperCase() as CountryCode];

  return (
    <div
      key={offer.Id}
      className={cx(styles.offer, {
        [styles.active]: isAnnual,
      })}
    >
      <div className={styles.row}>
        {periodTranslationKey && (
          <OfferBadge variant="period">{t(periodTranslationKey)}</OfferBadge>
        )}

        {savings && <OfferBadge variant="savings">{savings}</OfferBadge>}
      </div>

      <div className={styles.price}>
        <OfferPrice price={offer.Price} period={offer.Period} />

        {isAnnual && priceExplanationTranslationKey && (
          <span className={styles.priceSuffix}>
            {t(priceExplanationTranslationKey)}
          </span>
        )}
      </div>

      <Button
        href={signUpUrl}
        size="small"
        variant="subtle"
        className={styles.button}
      >
        {isAnnual
          ? t("web_landing_page_continue_annual")
          : t("web_landing_page_continue_monthly")}
      </Button>
    </div>
  );
};

export default LandingPageOffer;
